// =============================================================================
// components/discover/CoinCommunityCard.tsx — token-branded community card for
// the "Communities on CRYB" carousel (Discover hub, IMG_5878) + grid reuse.
//
// The reference cards read as SOLID, high-contrast blocks (black Solana / orange
// Bitcoin) — premium and crisp. We keep the CRYB family (deep purples/indigos,
// not the old per-coin brand colors) but SOLID, not a washed gradient, so they
// look sharp. Anatomy: logo + name (+ optional 1-line description) on top; the
// real member avatar-stack + count on the bottom. "Coin Holders" doesn't exist
// server-side, so the honest memberCount is the only figure shown.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import CachedImage from '../ui/CachedImage';
import { Pressable } from 'react-native';
import { spacing, radius, fonts } from '../../constants/theme';
import { Ionicons } from '../ui/Icon';
import AvatarStack from './AvatarStack';
import { compactCount } from './tokens';
import { communityIcon } from '../../lib/icons';

export interface CoinCommunity {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  avatar?: string;
  memberCount: number;
  joined?: boolean;
}

interface Props {
  community: CoinCommunity;
  onPress: (community: CoinCommunity) => void;
  /** Carousel card width (defaults to the reference's ~2-up peek layout). */
  width?: number;
  /** Directory/list usage: stretch to the row instead of a fixed width. */
  fullWidth?: boolean;
  /** Explicit card color (community-set). Falls back to the deterministic palette. */
  color?: string;
}

// Curated SOLID card colors — deep, saturated, premium; all dark enough for
// white text. Deterministic per community so a card's color is stable.
// Exported so the Create-Community color picker reuses the identical palette.
export const SOLIDS = [
  '#1B1636', // deep indigo-ink (the Solana-black premium feel)
  '#3A2C6E', // deep purple
  '#4B2A7B', // violet
  '#5A2A67', // plum
  '#262150', // slate purple
  '#6C3FA0', // CRYB purple
  '#7A3579', // orchid
  '#233A6B', // deep blue
];

export function solidFor(key: string): string {
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0;
  return SOLIDS[h % SOLIDS.length];
}

export default function CoinCommunityCard({ community, onPress, width = 250, fullWidth = false, color }: Props) {
  const bg = color ?? solidFor(community.name || community.id);
  const title = community.displayName || community.name || 'Community';
  const initial = (title.charAt(0) || 'C').toUpperCase();
  const desc = (community.description || '').trim();
  // Real coin logo when the name maps to a ticker; else the PNG-safe stored icon.
  const avatarUri = communityIcon(community.name, community.displayName, community.avatar);

  return (
    <Pressable
      onPress={() => onPress(community)}
      accessibilityRole="button"
      accessibilityLabel={`${title}, ${compactCount(community.memberCount)} members`}
      style={({ pressed }) => [
        styles.shadow,
        fullWidth ? styles.fullWidth : { width },
        pressed && { transform: [{ scale: 0.98 }] },
      ]}
    >
      <View style={[styles.card, { backgroundColor: bg }]}>
        {/* subtle top sheen for depth (very faint) */}
        <View style={styles.sheen} pointerEvents="none" />

        {/* Logo + name + optional description */}
        <View style={styles.topRow}>
          <View style={styles.logoCircle}>
            {avatarUri ? (
              <CachedImage source={{ uri: avatarUri }} style={styles.logoImg} />
            ) : (
              <Text style={styles.logoLetter}>{initial}</Text>
            )}
          </View>
          <View style={styles.titleBlock}>
            <Text style={styles.name} numberOfLines={1}>{title}</Text>
            <Text style={styles.sub} numberOfLines={1}>
              {desc || `${compactCount(community.memberCount)} ${community.memberCount === 1 ? 'member' : 'members'}`}
            </Text>
          </View>
        </View>

        {/* Member avatar-stack + count (reference bottom row) */}
        <View style={styles.bottomRow}>
          <AvatarStack seed={community.name || community.id} count={community.memberCount ?? 0} size={22} onDark />
          {community.joined ? (
            <View style={styles.joinedChip}>
              <Ionicons name="checkmark" size={12} color="#FFFFFF" />
              <Text style={styles.joinedChipText}>Joined</Text>
            </View>
          ) : (
            <View style={styles.arrowChip}>
              <Ionicons name="arrow-forward" size={15} color="#FFFFFF" />
            </View>
          )}
        </View>
      </View>
    </Pressable>
  );
}

const CARD_RADIUS = 20;

const styles = StyleSheet.create({
  shadow: {
    borderRadius: CARD_RADIUS,
    shadowColor: '#1A1A2E',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.22,
    shadowRadius: 16,
    elevation: 5,
  },
  fullWidth: { width: '100%' },
  card: {
    borderRadius: CARD_RADIUS,
    padding: spacing.lg,
    overflow: 'hidden',
    minHeight: 132,
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  sheen: {
    position: 'absolute',
    top: 0, left: 0, right: 0, height: 64,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  logoCircle: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: 'rgba(255,255,255,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  logoImg: { width: 46, height: 46, borderRadius: 23 },
  logoLetter: { fontSize: 20, color: '#FFFFFF', ...fonts.bold },
  titleBlock: { flex: 1 },
  name: { fontSize: 17.5, color: '#FFFFFF', ...fonts.bold },
  sub: {
    fontSize: 12.5,
    color: 'rgba(255,255,255,0.72)',
    marginTop: 3,
    ...fonts.medium,
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
  },
  joinedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  joinedChipText: { fontSize: 11, color: '#FFFFFF', ...fonts.bold },
  arrowChip: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
  },
});
